import { Option, RegionItem, SelectNameOption } from "@/types";
import { BadgeColor } from "@/types/domain/auction";
import { EXPANSION_MAPPING } from "./item";
import { getRealmDisplayLabel, isRussianRealm, REALMS_CLASSIC_ERA_EU, REALMS_CLASSIC_ERA_US, REALMS_EU, REALMS_US, RealmInfo } from "./realms";

export const SPECIAL_SUBCLASSES = ["Toys", "Mount", "Companions"] as const;

// WoW expansions for select dropdowns
export const expansionsInSelectFormat: Option[] = Object.entries(EXPANSION_MAPPING)
  .map(([key, label]) => ({
    value: Number(key),
    label: label
  }))
  .sort((a, b) => b.value - a.value);

// Item quality tiers
export const qualityTiers: Option[] = [
  { value: 999, label: "None" },
  { value: 0, label: "Poor" },
  { value: 1, label: "Common" },
  { value: 2, label: "Uncommon" },
  { value: 3, label: "Rare" },
  { value: 4, label: "Epic" },
  { value: 5, label: "Legendary" },
] as const;

export const craftsInSelectFormat: SelectNameOption[] = [
  {
    name: "included",
  },
  {
    name: "excluded",
  },
  {
    name: "exclusive",
  },
] as const;

// Available regions
export const regionsInSelectFormat: RegionItem[] = [
  {
    id: 1,
    name: "us",
  },
  {
    id: 2,
    name: "eu",
  },
] as const;

// US realm connections — derived from the canonical realms table in ./realms.
export const connectedRealmIDsToNamesUSInSelectFormat: Option[] = REALMS_US
  .map((r) => ({ value: r.id, label: getRealmDisplayLabel(r.id, "us") }))
  .sort((a, b) => a.label.localeCompare(b.label));

// EU realm connections — derived from the canonical realms table in ./realms.
export const connectedRealmIDsToNamesEUInSelectFormat: Option[] = REALMS_EU
  .map((r) => ({ value: r.id, label: getRealmDisplayLabel(r.id, "eu") }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Classic Era realm options. Classic realms are standalone (not connected), so
// the label is simply the realm's name(s).
const classicRealmLabel = (r: RealmInfo) => r.englishNames.join(", ");
export const classicEraRealmsUSInSelectFormat: Option[] = REALMS_CLASSIC_ERA_US
  .map((r) => ({ value: r.id, label: classicRealmLabel(r) }))
  .sort((a, b) => a.label.localeCompare(b.label));
export const classicEraRealmsEUInSelectFormat: Option[] = REALMS_CLASSIC_ERA_EU
  .map((r) => ({ value: r.id, label: classicRealmLabel(r) }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const russianServerIds: string[] = REALMS_EU
  .filter((r) => isRussianRealm(r.id, "eu"))
  .map((r) => r.id.toString());

export const colorVariants: Record<BadgeColor, { bg: string; text: string; border: string; arrow: string, ring: string }> = {
  white: {
    bg: 'bg-black-400/10',
    text: 'text-white/80',
    border: 'border-black-400/20',
    arrow: 'border-black-400/20',
    ring: "ring-black-400/20"
  },
  gray: {
    bg: 'bg-gray-400/10',
    text: 'text-gray-400',
    border: 'border-gray-400/20',
    arrow: 'border-gray-400/20',
    ring: "ring-gray-400/20"
  },
  red: {
    bg: 'bg-red-400/10',
    text: 'text-red-400',
    border: 'border-red-400/20',
    arrow: 'border-red-400/20',
    ring: "ring-red-400/20"
  },
  yellow: {
    bg: 'bg-yellow-400/10',
    text: 'text-yellow-500',
    border: 'border-yellow-400/20',
    arrow: 'border-yellow-400/20',
    ring: "ring-yellow-400/20"
  },
  green: {
    bg: 'bg-green-500/10',
    text: 'text-green-400',
    border: 'border-green-500/20',
    arrow: 'border-green-500/20',
    ring: "ring-green-500/20"
  },
  blue: {
    bg: 'bg-blue-400/10',
    text: 'text-blue-400',
    border: 'border-blue-400/30',
    arrow: 'border-blue-400/30',
    ring: "ring-blue-400/20"
  },
  purple: {
    bg: 'bg-purple-400/10',
    text: 'text-purple-400',
    border: 'border-purple-400/30',
    arrow: 'border-purple-400/30',
    ring: "ring-purple-400/20"
  },
  pink: {
    bg: 'bg-pink-400/10',
    text: 'text-pink-400',
    border: 'border-pink-400/20',
    arrow: 'border-pink-400/20',
    ring: "ring-pink-400/20"
  },
  orange: {
    bg: 'bg-orange-400/10',
    text: 'text-orange-400',
    border: 'border-orange-400/20',
    arrow: 'border-orange-400/20',
    ring: "ring-orange-400/20"
  },
};

export const FILTER_CONFIG = {
  SELL_RATE: {
    MIN: 0,
    MAX: 1,
    STEP: 0.001,
  },
  SALE_AVG: {
    MIN: 500,
    MAX: 10000000,
    STEP: 500,
  },
  GOLD_PROFIT_SALE_AVG: {
    MIN: 1000,
    MAX: 10000000,
    STEP: 1000,
  },
  SALE_AVG_VS_BUY_PERCENT: {
    MIN: -500,
    MAX: 1000000,
    STEP: 1,
  },
  GOLD_PROFIT: {
    MIN: 1000,
    MAX: 10000000,
    STEP: 1000,
  },
  GOLD_PROFIT_PERCENT: {
    MIN: 1,
    MAX: 10000000,
    STEP: 1,
  },
  GOLD_BUYOUT: {
    MIN: 0,
    MAX: 9999999,
    STEP: 100,
  },
  REGION_MARKET_VALUE: {
    MIN: 0,
    MAX: 10000000,
    STEP: 100,
  },
  REGION_HISTORICAL: {
    MIN: 0,
    MAX: 10000000,
    STEP: 100,
  },
  ILVL: {
    MIN: 0,
    MAX: 334,
    STEP: 1,
  },
  LVL: {
    MIN: 1,
    MAX: 90,
    STEP: 1,
  },
}